/**
 * Collection Filters Component
 * Vue-inspired state-based architecture for managing collection filters
 */

export default (node) => {
  // Element references
  const showFiltersBtn = node.querySelector('[data-show-filters]');
  const hideFiltersBtn = node.querySelector('[data-hide-filters]');
  const filtersContainer = node.querySelector('[data-filters-container]');
  const clearFiltersBtn = node.querySelector('[data-clear-filters]');
  const filterElements = node.querySelectorAll('[data-filter-tag]');

  // State object (Vue-inspired)
  const state = {
    collection: '',
    selectedFilters: {},
  };

  /**
   * Parse URL filters on initialization
   * Format: /collections/{handle}/{filter1}+{filter2}+...
   * Regex inspired by Vue component
   */
  const parseUrlFilters = () => {
    const TAGS_REGEX = /\/collections\/([^\/]*)(\/([^\/?]*))?/;
    const pathname = window.location.pathname;
    const match = pathname.match(TAGS_REGEX);

    if (!match) return;

    state.collection = match[1];
    const tags = match[3] ? match[3].split('+') : [];

    // Initialize selectedFilters based on URL
    tags.forEach((tag) => {
      const [filterTag, filterValue] = tag.split(':');
      if (filterTag && filterValue) {
        state.selectedFilters[filterTag] = filterValue;
      }
    });
  };

  /**
   * Update filter value in state
   * @param {string} filterTag - Filter identifier (e.g., 'filter-color')
   * @param {string} filterValue - Selected value
   */
  const updateFilterValue = (filterTag, filterValue) => {
    if (!filterValue || filterValue === '') {
      // Remove filter if value is empty
      delete state.selectedFilters[filterTag];
    } else {
      // Update filter value
      state.selectedFilters[filterTag] = filterValue;
    }
  };

  /**
   * Build filter URL and navigate
   * Simplified logic inspired by Vue component
   */
  const handleFilterChange = () => {
    // Build filters array from state
    const filters = Object.entries(state.selectedFilters)
      .filter(([_, value]) => value) // Filter out empty values
      .map(([tag, value]) => `${tag}:${value}`)
      .join('+');

    // Build new URL
    const newPath = filters
      ? `/collections/${state.collection}/${filters}`
      : `/collections/${state.collection}`;

    window.location.pathname = newPath;
  };

  /**
   * Handle select filter change
   * @param {HTMLElement} filterElement - Filter element (select or color group)
   */
  const handleFilterSelect = (filterElement) => {
    const filterTag = filterElement.dataset.filterTag;
    let filterValue = '';

    // Check if it's a color filter (radio buttons)
    const colorRadio = filterElement.querySelector(
      '[data-color-filter-radio]:checked'
    );
    if (colorRadio) {
      filterValue = colorRadio.value;
    } else if (filterElement.tagName === 'SELECT') {
      // Regular select element
      filterValue = filterElement.value;
    }

    updateFilterValue(filterTag, filterValue);
    handleFilterChange();
  };

  /**
   * Clear all filters and return to base collection
   */
  const clearAllFilters = () => {
    state.selectedFilters = {};
    window.location.pathname = `/collections/${state.collection}`;
  };

  /**
   * Toggle filters visibility (mobile)
   */
  const toggleFiltersVisibility = (show) => {
    if (!filtersContainer) return;

    if (show) {
      filtersContainer.classList.remove('hidden');
      showFiltersBtn?.classList.add('!hidden');
      hideFiltersBtn?.classList.remove('!hidden');
    } else {
      filtersContainer.classList.add('hidden');
      showFiltersBtn?.classList.remove('!hidden');
      hideFiltersBtn?.classList.add('!hidden');
    }
  };

  /**
   * Initialize component
   */
  const init = () => {
    // Parse initial state from URL
    parseUrlFilters();

    // Show/hide filters buttons
    if (showFiltersBtn) {
      showFiltersBtn.addEventListener('click', () =>
        toggleFiltersVisibility(true)
      );
    }

    if (hideFiltersBtn) {
      hideFiltersBtn.addEventListener('click', () =>
        toggleFiltersVisibility(false)
      );
    }

    // Clear filters button
    if (clearFiltersBtn) {
      clearFiltersBtn.addEventListener('click', clearAllFilters);
    }

    // Filter change listeners
    filterElements.forEach((filterElement) => {
      // Handle select elements
      if (filterElement.tagName === 'SELECT') {
        filterElement.addEventListener('change', () =>
          handleFilterSelect(filterElement)
        );
      }

      // Handle color filter radio buttons
      const radioButtons = filterElement.querySelectorAll(
        '[data-color-filter-radio]'
      );
      radioButtons.forEach((radio) => {
        radio.addEventListener('change', () =>
          handleFilterSelect(filterElement)
        );
      });
    });
  };

  init();
};
